<div class="session-detail-page">
  <div class="detail-page-header">
    <div>
      <p-button
        label="Back to Sessions"
        icon="pi pi-arrow-left"
        [text]="true"
        routerLink="/sessions"
      ></p-button>
      <h1 class="detail-page-title">Session Details</h1>
    </div>
  </div>

  @if (loading()) {
    <p-card styleClass="state-card">
      <div class="state-content">
        <i class="pi pi-spin pi-spinner state-icon"></i>
        <div>
          <div class="state-title">Loading session details</div>
          <div class="state-copy">Fetching the session data for this URL.</div>
        </div>
      </div>
    </p-card>
  } @else if (detail(); as detailWrapper) {
    <div class="detail-shell">
      @if (errorMessage(); as error) {
        <app-error-banner [message]="error" />
      }
      <div class="detail-summary">
        <div
          class="flex justify-content-between align-items-center mb-5 px-1 border-bottom-1 surface-border pb-3 detail-summary-header"
        >
          <div class="flex gap-3 align-items-center">
            <p-tag
              [value]="detailWrapper.type"
              [severity]="detailWrapper.type === 'LIVE' ? 'warn' : 'info'"
              styleClass="text-base px-3 py-1"
            ></p-tag>
            <div class="flex flex-column">
              @if (liveDetail()?.meetingTitle) {
                <span class="text-xl font-bold text-900">
                  {{ liveDetail()?.meetingTitle }}
                </span>
              }
              <span
                [class.text-xl]="!liveDetail()?.meetingTitle"
                [class.font-bold]="!liveDetail()?.meetingTitle"
                [class.text-900]="!liveDetail()?.meetingTitle"
                [class.text-sm]="liveDetail()?.meetingTitle"
                [class.text-500]="liveDetail()?.meetingTitle"
              >
                {{ detailWrapper.data.createdAt | date: 'medium' }}
              </span>
              <span class="text-sm text-500">ID: {{ detailWrapper.data.id }}</span>
            </div>
          </div>
          <div class="flex gap-2 align-items-center detail-summary-actions">
            @if (detailWrapper.type === 'LIVE') {
              <p-button
                icon="pi pi-download"
                label="Export E2E Scenario"
                severity="secondary"
                [outlined]="true"
                size="small"
                (onClick)="downloadScenario()"
              ></p-button>
            }
            <p-tag
              [value]="detailWrapper.data.status"
              [severity]="getStatusSeverity(detailWrapper.data.status)"
              styleClass="text-base"
            ></p-tag>
          </div>
        </div>

        <div class="grid-container">
          <div class="chat-column">
            <p-card styleClass="session-detail-card h-full" [style]="{ overflow: 'hidden' }">
              <p-tabs
                [value]="detailWrapper.type === 'LIVE' ? 'timeline' : 'chat'"
                styleClass="session-tabs h-full flex flex-column"
              >
                <p-tablist>
                  @if (detailWrapper.type === 'LIVE') {
                    <p-tab value="timeline">Timeline</p-tab>
                  }
                  <p-tab value="chat">Conversation</p-tab>
                  @if (detailWrapper.type === 'LIVE') {
                    <p-tab value="transcript">Live Transcript</p-tab>
                    <p-tab value="post-transcript">Post-Session Transcript</p-tab>
                  }
                  <p-tab value="llm">LLM Trace</p-tab>
                </p-tablist>
                <p-tabpanels styleClass="flex-1 overflow-hidden p-0 h-full">
                  <p-tabpanel value="chat" styleClass="h-full p-0">
                    <div class="chat-scroll-area h-full">
                      @for (msg of chatMessages(); track msg.id) {
                        <div
                          class="chat-message"
                          [class.user-msg]="msg.role === 'user'"
                          [class.assistant-msg]="msg.role === 'assistant'"
                        >
                          <div class="msg-bubble">
                            <div class="msg-sender">{{ msg.role }}</div>
                            <div class="msg-content">{{ formatMessageContent(msg.content) }}</div>
                            <div class="msg-time">{{ msg.createdAt | date: 'shortTime' }}</div>
                          </div>
                        </div>
                      }
                      @if (chatMessages().length === 0) {
                        <div
                          class="flex flex-column align-items-center justify-content-center h-full text-500"
                        >
                          <i class="pi pi-comments text-4xl mb-3"></i>
                          <span>No chat history available.</span>
                        </div>
                      }
                    </div>
                  </p-tabpanel>
                  @if (detailWrapper.type === 'LIVE') {
                    <p-tabpanel value="transcript" styleClass="h-full p-0">
                      <div class="flex flex-column h-full">
                        @if (liveTranscript().length > 0) {
                          <div
                            class="flex justify-content-end px-3 pt-3 border-bottom-1 surface-border pb-2"
                          >
                            <p-button
                              icon="pi pi-download"
                              label="Download .txt"
                              severity="secondary"
                              [text]="true"
                              size="small"
                              (onClick)="downloadTranscript('live')"
                            ></p-button>
                          </div>
                        }
                        <div class="chat-scroll-area flex-1">
                          @for (item of liveTranscript(); track item.timestamp) {
                            <div class="mb-2 p-3 surface-card border-round shadow-1">
                              <div class="flex justify-content-between text-xs text-500 mb-2">
                                <span
                                  class="font-bold uppercase tracking-wide"
                                  [class.text-primary]="item.speaker === 'Agent'"
                                  >{{ item.speaker }}</span
                                >
                                <span>{{ item.timestamp | date: 'mediumTime' }}</span>
                              </div>
                              <div class="text-900 line-height-3 font-medium">{{ item.text }}</div>
                            </div>
                          }
                          @if (liveTranscript().length === 0) {
                            <div
                              class="flex flex-column align-items-center justify-content-center h-full text-500"
                            >
                              <i class="pi pi-microphone text-4xl mb-3"></i>
                              <span>No transcript available.</span>
                            </div>
                          }
                        </div>
                      </div>
                    </p-tabpanel>
                    <p-tabpanel value="post-transcript" styleClass="h-full p-0">
                      <div class="flex flex-column h-full">
                        <div
                          class="flex justify-content-between align-items-center gap-2 px-3 pt-3 border-bottom-1 surface-border pb-2"
                        >
                          <div class="flex align-items-center gap-2">
                            @if (postSessionStatus()) {
                              <p-tag
                                [value]="postSessionStatus()!"
                                [severity]="getPostSessionSeverity(postSessionStatus())"
                                styleClass="uppercase"
                              ></p-tag>
                            }
                          </div>
                          @if (postSessionTranscript().length > 0 || postSession()?.transcript) {
                            <div class="flex gap-1">
                              <p-button
                                icon="pi pi-download"
                                label="Download .txt"
                                severity="secondary"
                                [text]="true"
                                size="small"
                                (onClick)="downloadTranscript('post')"
                              ></p-button>
                              <p-button
                                icon="pi pi-code"
                                label="Download .json"
                                severity="secondary"
                                [text]="true"
                                size="small"
                                (onClick)="downloadPostSessionJson()"
                              ></p-button>
                            </div>
                          }
                        </div>
                        <div class="chat-scroll-area flex-1">
                          @if (postSessionTranscript().length > 0) {
                            @for (item of postSessionTranscript(); track item.id) {
                              <div class="mb-2 p-3 surface-card border-round shadow-1">
                                <div class="flex justify-content-between text-xs text-500 mb-2">
                                  <span
                                    class="font-bold uppercase tracking-wide"
                                    [class.text-primary]="item.speaker === 'Agent'"
                                    >{{ item.speaker }}</span
                                  >
                                  <span>{{ item.timestamp / 1000 | number: '1.0-0' }}s</span>
                                </div>
                                <div class="text-900 line-height-3 font-medium">
                                  {{ item.text }}
                                </div>
                              </div>
                            }
                          } @else if (postSession()?.transcript) {
                            <pre
                              class="text-900 line-height-3 white-space-pre-wrap m-0 font-sans"
                              >{{ postSession()?.transcript }}</pre>
                          } @else {
                            <div
                              class="flex flex-column align-items-center justify-content-center h-full text-500"
                            >
                              <i class="pi pi-file-edit text-4xl mb-3"></i>
                              <span>
                                @switch (postSessionStatus()) {
                                  @case ('pending') {
                                    Post-session transcription is queued.
                                  }
                                  @case ('running') {
                                    Post-session transcription is in progress.
                                  }
                                  @case ('failed') {
                                    Post-session transcription failed.
                                  }
                                  @case ('skipped') {
                                    Post-session transcription was skipped.
                                  }
                                  @default {
                                    No post-session transcript available.
                                  }
                                }
                              </span>
                            </div>
                          }
                        </div>
                      </div>
                    </p-tabpanel>
                  }
                  @if (detailWrapper.type === 'LIVE') {
                    <p-tabpanel value="timeline" styleClass="h-full p-0">
                      <app-session-timeline
                        [transcription]="liveTranscript()"
                        [suggestionHistory]="liveDetail()?.suggestionHistory || []"
                        [sessionCreatedAt]="detailWrapper.data.createdAt"
                        [sessionDurationSeconds]="liveDetail()?.durationInSeconds || 0"
                      ></app-session-timeline>
                    </p-tabpanel>
                  }
                  <p-tabpanel value="llm" styleClass="h-full p-0 overflow-y-auto">
                    <div class="p-4">
                      <div class="llm-summary-grid mb-4">
                        <div class="summary-card">
                          <div class="text-sm font-medium text-500">Total Calls</div>
                          <div class="text-3xl font-bold">{{ llmTraceSummary().totalCalls }}</div>
                        </div>
                        <div class="summary-card">
                          <div class="text-sm font-medium text-500">Total Cost</div>
                          <div class="text-3xl font-bold">
                            {{ llmTraceTotalCost() | currency: 'EUR' : 'symbol' : '1.4-4' }}
                          </div>
                        </div>
                      </div>
                      @if (llmTracePerModelSummary().length > 0) {
                        <div class="mb-4">
                          <h4 class="mb-3 text-sm font-bold uppercase text-500 tracking-wider">
                            Per-Model Breakdown
                          </h4>
                          <div class="per-model-grid">
                            @for (modelStats of llmTracePerModelSummary(); track modelStats.model) {
                              <div class="per-model-card">
                                <div class="flex align-items-center justify-content-between">
                                  <div class="font-semibold text-900">{{ modelStats.model }}</div>
                                  <div class="model-calls-badge">
                                    {{ modelStats.calls }}
                                    <span class="text-xs"
                                      >call{{ modelStats.calls === 1 ? '' : 's' }}</span
                                    >
                                  </div>
                                </div>
                                <div class="flex justify-content-end align-items-center mt-2">
                                  <div class="model-cost-badge">
                                    {{ modelStats.cost | currency: 'EUR' : 'symbol' : '1.4-4' }}
                                  </div>
                                </div>
                              </div>
                            }
                          </div>
                        </div>
                      }
                      <p-table
                        [value]="llmLogs()"
                        styleClass="p-datatable-sm llm-trace-table"
                        [scrollable]="true"
                        scrollHeight="flex"
                      >
                        <ng-template pTemplate="header">
                          <tr>
                            <th style="width: 80px">Time</th>
                            <th>Prompt</th>
                            <th>Model</th>
                            <th class="text-right">Tokens</th>
                            <th class="text-right">Cost</th>
                            <th class="text-center" style="width: 60px">Status</th>
                          </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-log>
                          <tr>
                            <td class="white-space-nowrap text-500 font-mono text-xs">
                              {{ log.createdAt | date: 'HH:mm:ss' }}
                            </td>
                            <td>
                              <span class="text-sm font-semibold text-900">{{
                                log.promptName || 'Unknown'
                              }}</span>
                            </td>
                            <td class="text-sm text-600">{{ log.model }}</td>
                            <td class="text-right text-sm font-mono">
                              {{ log.totalTokens | number }}
                            </td>
                            <td class="text-right text-sm font-mono">
                              {{ log.totalCost | currency: 'EUR' : 'symbol' : '1.4-4' }}
                            </td>
                            <td class="text-center">
                              <i
                                class="pi"
                                [class.pi-times-circle]="log.isError"
                                [class.text-red-500]="log.isError"
                                [class.pi-check-circle]="!log.isError"
                                [class.text-green-500]="!log.isError"
                              ></i>
                            </td>
                          </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                          <tr>
                            <td colspan="6" class="text-center p-5 text-500">
                              No LLM logs found for this session.
                            </td>
                          </tr>
                        </ng-template>
                      </p-table>
                    </div>
                  </p-tabpanel>
                </p-tabpanels>
              </p-tabs>
            </p-card>
          </div>

          <div class="info-column">
            @if (practiceDetail(); as pd) {
              @if (pd.courseContext; as course) {
                <p-card header="Practice Course" styleClass="session-detail-card">
                  <div class="flex flex-column gap-2">
                    <a
                      class="link-cell text-lg"
                      [routerLink]="['/practice-courses', course.courseVersionId]"
                      >{{ course.courseName }} · v{{ course.version }}</a
                    >
                    <div class="flex justify-content-between border-bottom-1 surface-border pb-2">
                      <span class="text-600">Module</span><strong>{{ course.moduleName }}</strong>
                    </div>
                    <div class="flex justify-content-between border-bottom-1 surface-border pb-2">
                      <span class="text-600">Scenario range</span
                      ><strong class="text-right">{{ course.scenarioRangeName }}</strong>
                    </div>
                    <div class="flex justify-content-between border-bottom-1 surface-border pb-2">
                      <span class="text-600">Target skills</span
                      ><span class="text-right">{{ course.targetSkills.join(', ') || '—' }}</span>
                    </div>
                    <div class="flex justify-content-between border-bottom-1 surface-border pb-2">
                      <span class="text-600">Course result</span
                      ><p-tag
                        [value]="
                          course.passed === null ? 'Pending' : course.passed ? 'Passed' : 'Failed'
                        "
                        [severity]="
                          course.passed
                            ? 'success'
                            : course.passed === false
                              ? 'danger'
                              : 'secondary'
                        "
                      />
                    </div>
                    <div>
                      <span class="text-600 block mb-1">Critical failures</span
                      ><span>{{ course.criticalFailures.join(', ') || 'None' }}</span>
                    </div>
                    <div>
                      <span class="text-600 block mb-1">Recommended next action</span
                      ><span>{{ course.recommendedNextAction || '—' }}</span>
                    </div>
                  </div>
                </p-card>
              }
              <p-card header="Evaluation Results" styleClass="session-detail-card">
                @if (pd.evaluation?.evaluation; as ev) {
                  <div>
                    <div
                      class="flex align-items-center justify-content-between mb-4 p-3 surface-ground border-round"
                    >
                      <span class="text-xl font-bold">Overall Score</span>
                      <p-tag
                        [value]="ev.score + '/10'"
                        severity="success"
                        styleClass="text-xl px-3 py-2"
                      ></p-tag>
                    </div>
                    <h4 class="mb-2 text-color-secondary font-medium uppercase text-sm">
                      Rubric Breakdown
                    </h4>
                    <div class="rubric-grid mb-4">
                      <div class="rubric-item">
                        <span>Clarity</span><strong>{{ ev.rubric.clarity }}</strong>
                      </div>
                      <div class="rubric-item">
                        <span>Empathy</span><strong>{{ ev.rubric.empathy }}</strong>
                      </div>
                      <div class="rubric-item">
                        <span>Engagement</span><strong>{{ ev.rubric.engagement }}</strong>
                      </div>
                      <div class="rubric-item">
                        <span>Relevance</span><strong>{{ ev.rubric.relevance }}</strong>
                      </div>
                      <div class="rubric-item">
                        <span>Pro</span><strong>{{ ev.rubric.professionalism }}</strong>
                      </div>
                    </div>
                    <div class="flex flex-column gap-3">
                      <div>
                        <strong class="block mb-1 text-900">Strengths</strong>
                        <p class="m-0 text-600 line-height-3">{{ ev.strengths }}</p>
                      </div>
                      <div>
                        <strong class="block mb-1 text-900">Improvements</strong>
                        <p class="m-0 text-600 line-height-3">{{ ev.improvements }}</p>
                      </div>
                    </div>
                  </div>
                }
              </p-card>
              <p-card header="Session Config" styleClass="session-detail-card">
                <div class="flex flex-column gap-2">
                  <div class="flex justify-content-between border-bottom-1 surface-border pb-2">
                    <span class="text-600">Playbook</span>
                    <span class="font-semibold">{{ pd.salesPlaybook?.name || 'N/A' }}</span>
                  </div>
                  <div class="flex justify-content-between border-bottom-1 surface-border pb-2">
                    <span class="text-600">ICP</span>
                    <span class="font-semibold">{{ pd.idealCustomerProfile?.name || 'N/A' }}</span>
                  </div>
                  <div class="flex justify-content-between border-bottom-1 surface-border pb-2">
                    <span class="text-600">Persona</span>
                    <span class="font-semibold text-right"
                      >{{ pd.persona.name }} <br />
                      <span class="text-sm text-500 font-normal"
                        >{{ pd.persona.role }} at {{ pd.persona.company }}</span
                      ></span
                    >
                  </div>
                  <div class="flex justify-content-between">
                    <span class="text-600">Difficulty</span>
                    <p-tag [value]="getDifficulty(pd.config)" severity="warn"></p-tag>
                  </div>
                </div>
              </p-card>
            }

            @if (liveDetail(); as ld) {
              <p-card header="Live Analysis" styleClass="session-detail-card h-full">
                <div class="mb-4">
                  @if (ld.meetingTitle) {
                    <div class="flex align-items-center justify-content-between mb-3">
                      <span class="text-600 font-medium">Meeting Title</span>
                      <span class="font-bold text-900 text-right" style="max-width: 60%">{{
                        ld.meetingTitle
                      }}</span>
                    </div>
                  }
                  <div class="flex align-items-center justify-content-between mb-3">
                    <span class="text-600 font-medium">Active Playbook</span>
                    <span class="font-bold text-900 text-right" style="max-width: 60%">{{
                      ld.playbookName || 'None'
                    }}</span>
                  </div>
                  @if (llmCallCounts().length) {
                    <div>
                      <span class="text-xs font-bold text-500 uppercase mb-2 block"
                        >LLM Activity</span
                      >
                      <div class="meta-grid">
                        @for (metric of llmCallCounts(); track metric.label) {
                          <div class="meta-item">
                            <span class="label">{{ metric.label }}</span>
                            <span class="value">{{ metric.count }}</span>
                          </div>
                        }
                      </div>
                    </div>
                  }
                </div>
                <p-divider></p-divider>
                <p-tabs value="0" styleClass="session-tabs">
                  <p-tablist>
                    <p-tab value="0">Meeting Notes</p-tab>
                    <p-tab value="1">Summary</p-tab>
                    <p-tab value="2">Suggestion Log</p-tab>
                  </p-tablist>
                  <p-tabpanels>
                    <p-tabpanel value="0">
                      @if (ld.meetingNotes) {
                        <div class="flex flex-column gap-4 pt-2">
                          @if (ld.meetingNotes.actionItems?.length) {
                            <div>
                              <h4 class="m-0 mb-2 text-primary-700">Action Items</h4>
                              <ul class="pl-3 m-0 line-height-3">
                                @for (item of ld.meetingNotes.actionItems; track $index) {
                                  <li class="mb-1">
                                    {{ item }}
                                  </li>
                                }
                              </ul>
                            </div>
                          }
                          @if (ld.meetingNotes.keyDecisions?.length) {
                            <div>
                              <h4 class="m-0 mb-2 text-primary-700">Key Decisions</h4>
                              <ul class="pl-3 m-0 line-height-3">
                                @for (item of ld.meetingNotes.keyDecisions; track $index) {
                                  <li class="mb-1">
                                    {{ item }}
                                  </li>
                                }
                              </ul>
                            </div>
                          }
                          @if (
                            !ld.meetingNotes.actionItems?.length &&
                            !ld.meetingNotes.keyDecisions?.length
                          ) {
                            <div class="text-500 italic">No specific notes extracted.</div>
                          }
                        </div>
                      } @else {
                        <div class="text-center p-4 text-500">No meeting notes recorded.</div>
                      }
                    </p-tabpanel>
                    <p-tabpanel value="1">
                      <div class="p-3 surface-ground border-round line-height-3 text-900">
                        {{ ld.conversationSummary || 'No summary generated yet.' }}
                      </div>
                    </p-tabpanel>
                    <p-tabpanel value="2">
                      <div class="suggestion-list h-full overflow-y-auto" style="max-height: 400px">
                        @for (sug of ld.suggestionHistory; track $index) {
                          <div class="suggestion-card">
                            <div class="flex justify-content-between align-items-start mb-2">
                              <span class="trigger-badge">{{ sug.triggerType }}</span>
                              <span class="text-xs text-500">{{
                                sug.timestamp | date: 'mm:ss'
                              }}</span>
                            </div>
                            <div class="font-semibold text-900 mb-1">{{ sug.suggestion }}</div>
                            <div class="text-sm text-600">{{ sug.explanation }}</div>
                          </div>
                        }
                        @if (!ld.suggestionHistory?.length) {
                          <div class="text-center p-4 text-500">No suggestions triggered.</div>
                        }
                      </div>
                    </p-tabpanel>
                  </p-tabpanels>
                </p-tabs>
              </p-card>
            }
          </div>
        </div>
      </div>
    </div>
  } @else {
    <p-card styleClass="state-card">
      <div class="state-content">
        <i class="pi pi-exclamation-circle state-icon error"></i>
        <div>
          <div class="state-title">Session unavailable</div>
          <div class="state-copy">
            {{ errorMessage() || 'This session could not be loaded with your current access.' }}
          </div>
        </div>
      </div>
    </p-card>
  }
</div>
